import './theme.css';
import type { AgentLogo } from '@/app/lib/stage/StageHeader.tsx';
import { defineMessages, type MessageDescriptor } from 'react-intl';

const siteMessages = defineMessages({
  problemPlaceholder: {
    id: 'agent.problemPlaceholder',
    defaultMessage: "Tell me what's going on…",
    description: 'Invitation shown inside the empty composer.',
  },
  preChatNotice: {
    id: 'agent.preChatNotice',
    defaultMessage:
      "You're chatting with an AI assistant, not with Jason. It runs on Agentplace, which stores this conversation to provide the service. Nothing you type is sent to Jason automatically; if you'd like to talk, copy your brief at the end and email it yourself. Please don't share passwords, account or card numbers, health information, or confidential details. Jason for You offers technology and business consulting, not legal services. By continuing, you agree to this and to <privacyPolicy>Agentplace's privacy policy</privacyPolicy>.",
    description: 'Required AI and privacy notice shown before the visitor sends a message.',
  },
  brandDescriptor: {
    id: 'agent.brandDescriptor',
    defaultMessage: 'An AI, not Jason',
    description: 'Short identity line under the AI advisor title.',
  },
  contactLabel: {
    id: 'agent.contactLabel',
    defaultMessage: 'Write to Jason',
    description: 'Header mailto button label.',
  },
  advisorLabel: {
    id: 'agent.advisorLabel',
    defaultMessage: 'AI advisor',
    description: 'Label shown above each advisor reply in the conversation log.',
  },
  welcomeEyebrow: {
    id: 'agent.welcomeEyebrow',
    defaultMessage: 'Talk it through',
    description: 'Small eyebrow shown above the welcome response.',
  },
  postInputNote: {
    id: 'agent.postInputNote',
    defaultMessage:
      "This is Jason's AI advisor, not Jason. Keep anything confidential for your conversation with Jason.",
    description: 'Brief confidentiality reminder shown below the input before the first message.',
  },
  errorMessage: {
    id: 'agent.errorMessage',
    defaultMessage: "That didn't go through. Try again, or email Jason at {email}.",
    description: 'Retry and direct-contact copy shown when a message fails.',
  },
  starterOne: {
    id: 'agent.starterOne',
    defaultMessage: 'We do the same thing by hand every week.',
    description: 'First optional starter message on the welcome screen.',
  },
  starterTwo: {
    id: 'agent.starterTwo',
    defaultMessage: "It's in there somewhere.",
    description: 'Second optional starter message on the welcome screen.',
  },
  starterThree: {
    id: 'agent.starterThree',
    defaultMessage: 'Customers fall through the cracks.',
    description: 'Third optional starter message on the welcome screen.',
  },
  starterFour: {
    id: 'agent.starterFour',
    defaultMessage: "I don't know what I'm paying for.",
    description: 'Fourth optional starter message on the welcome screen.',
  },
  starterFive: {
    id: 'agent.starterFive',
    defaultMessage: 'I have an idea for a new tool or service.',
    description: 'Fifth optional starter message on the welcome screen.',
  },
  starterSix: {
    id: 'agent.starterSix',
    defaultMessage: 'Everyone says we should use AI.',
    description: 'Sixth optional starter message on the welcome screen.',
  },
});

const advisorEmail = 'jason@orcasolutions.dev';

export interface SiteConfig {
  /** The customer-facing identity used by embedded entry controls. */
  brandName: string;
  headerBrandName?: string;
  brandDescriptor?: MessageDescriptor;
  /** Original Jason for You stamp mark, displayed without recoloring or cropping. */
  logo?: AgentLogo;
  logoHref?: string;
  contactLabel?: MessageDescriptor;
  contactHref?: string;
  advisorLabel?: MessageDescriptor;
  welcomeEyebrow?: MessageDescriptor;
  starterPrompts?: readonly MessageDescriptor[];
  /** The assistant's appearance. 'auto' follows the visitor's OS. */
  appearance: 'light' | 'dark' | 'auto';
  /** Omnibox placeholder override. */
  placeholder?: MessageDescriptor;
  /** Required disclosure shown before the visitor sends a message. */
  preChatNotice?: MessageDescriptor;
  /** Short reminder below the input, shown before the first message. */
  postInputNote?: MessageDescriptor;
  /** Error copy for failed message delivery, with a mailto link supplied by the shell. */
  errorMessage?: MessageDescriptor;
  emailAddress?: string;
}

export const SITE_CONFIG: SiteConfig = {
  brandName: 'AI advisor',
  headerBrandName: 'AI advisor',
  brandDescriptor: siteMessages.brandDescriptor,
  logo: {
    src: '/jason-for-you-stamp.svg',
    alt: 'Jason for You stamp monogram',
  },
  logoHref: 'https://jasonforyou.com/',
  contactLabel: siteMessages.contactLabel,
  contactHref: `mailto:${advisorEmail}`,
  advisorLabel: siteMessages.advisorLabel,
  welcomeEyebrow: siteMessages.welcomeEyebrow,
  starterPrompts: [
    siteMessages.starterOne,
    siteMessages.starterTwo,
    siteMessages.starterThree,
    siteMessages.starterFour,
    siteMessages.starterFive,
    siteMessages.starterSix,
  ],
  appearance: 'light',
  placeholder: siteMessages.problemPlaceholder,
  preChatNotice: siteMessages.preChatNotice,
  postInputNote: siteMessages.postInputNote,
  errorMessage: siteMessages.errorMessage,
  emailAddress: advisorEmail,
};
